import { createEntityAdapter, createSelector, createSlice } from '@reduxjs/toolkit';

import { isScheduleAvailable } from 'FRONTEND/components/Calendar/utils';
import { selectTypeFilterList } from 'FRONTEND/reducers/calendarSlice';
import {
  APPOINTMENT_TYPE_LIST,
  RESOURCE_APPOINTMENT_TYPE_LIST,
  CREATE_ERROR,
  DELETE_ERROR,
  DRAFT_AVAILABILITY_ID,
  LIST_ERROR,
  UPDATE_ERROR,
  EXPERT,
  RESOURCE,
} from 'FRONTEND/utils/constants';

const availabilityAdapter = createEntityAdapter({
  selectId: ({ availabilityId }) => availabilityId,
  sortComparer: (a, b) => a.start > b.start ? 1 : a.start < b.start ? -1 : 0,
});

const initialState = {
  ...availabilityAdapter.getInitialState(),
  errorType: null,
  error: null,
};

// Relative selectors for usage in the reducer
const { selectAll: selectAllAvailabilitiesRelatively, selectById: selectAvailabilityByIdRelatively } = availabilityAdapter.getSelectors(); // Selector from the reducer's scope for usage in the reducer.
const selectExpertAvailabilityIdListRelatively = createSelector(
  selectAllAvailabilitiesRelatively,
  (_, expertId) => expertId,
  (availabilityList, expertId) => {
    return availabilityList.filter((availability) => availability.actorId === expertId).map((availability) => availability.availabilityId);
  },
);

export const selectActorAvailabilitiesRelatively = createSelector(
  selectAllAvailabilitiesRelatively,
  (_, actorId) => actorId,
  (availabilityList, actorId) => availabilityList.filter((availability) => availability.actorId === actorId),
);

const availabilitySlice = createSlice({
  name: 'availability',
  initialState,
  reducers: {
    listRequest(state) {},
    listSuccess(state, { payload: { availabilities } }) {
      availabilityAdapter.upsertMany(state, availabilities);
    },
    listError(state, { payload: { error } }) {
      state.errorType = LIST_ERROR;
      state.error = error;
    },
    clear(state, { payload: { actorId } }) {
      if (actorId) {
        availabilityAdapter.removeMany(state, selectExpertAvailabilityIdListRelatively(state, actorId));
      } else {
        availabilityAdapter.removeAll(state);
      }
    },
    createDraft(state, { payload }) { // Draft is for creation only (does not relate to edition)
      if (payload.originalAvailabilityId || isScheduleAvailable({
        availabilities: selectActorAvailabilitiesRelatively(state, payload.actorId),
        startDate: payload.startDate,
        endDate: payload.endDate,
      })) {
        availabilityAdapter.addOne(state, {
          availabilityId: DRAFT_AVAILABILITY_ID,
          actorId: payload.actorId,
          actorType: payload.actorType === EXPERT ? EXPERT : RESOURCE,
          startDate: payload.startDate,
          endDate: payload.endDate,
          title: payload.title,
          typeList: payload.actorType === EXPERT ? APPOINTMENT_TYPE_LIST : RESOURCE_APPOINTMENT_TYPE_LIST,
          tenantList: payload.tenantList || [],
        });
      }
    },
    deleteDraft(state) {
      availabilityAdapter.removeOne(state, DRAFT_AVAILABILITY_ID);
    },
    startEditing(state, { payload: { id } }) {
      const originalAvailability = selectAvailabilityByIdRelatively(state, id);
      availabilityAdapter.updateOne(state, { id, changes: { isEditing: true, originalAvailability } });
    },
    edit(state, { payload: { availabilityId, ...changes } }) { // Pending modification, not committed to the server and database yet
      availabilityAdapter.updateOne(state, { id: availabilityId, changes });
    },
    stopEditing(state, { payload: { id, shouldRestore } }) {
      if (shouldRestore) {
        const availability = selectAvailabilityByIdRelatively(state, id);
        availabilityAdapter.updateOne(state, { id, changes: { ...availability.originalAvailability, isEditing: false } });
      } else {
        availabilityAdapter.updateOne(state, { id, changes: { isEditing: false, originalAvailability: null } });
      }
    },
    createRequest(state, { payload }) {},
    createSuccess(state, { payload: { availability } }) {
      if (availability) {
        availabilityAdapter.addOne(state, availability);
        availabilityAdapter.removeOne(state, DRAFT_AVAILABILITY_ID);
      }
    },
    createError(state, { payload: { error } }) {
      state.errorType = CREATE_ERROR;
      state.error = error;
    },
    updateRequest(state, { payload: { availabilityId, ...changes } }) {},
    updateSuccess(state, { payload: { availability: { availabilityId, ...changes } } }) {
      availabilityAdapter.updateOne(state, { id: availabilityId, changes });
    },
    updateError(state, { payload }) {
      state.errorType = UPDATE_ERROR;
      state.error = payload.error;
    },
    deleteRequest(state, { payload: { availabilityId } }) {},
    deleteSuccess(state, { payload: { availabilityId } }) {
      availabilityAdapter.removeOne(state, availabilityId);
    },
    deleteError(state, { payload }) {
      state.errorType = DELETE_ERROR;
      state.error = payload.error;
    },
    createRecurrenceRequest(state, { payload }) {},
    createRecurrenceSuccess(state, { payload: { availabilities } } = {}) {
      if (availabilities) {
        availabilityAdapter.addMany(state, availabilities);
      }
    },
    createRecurrenceError(state, { payload: { error } }) {
      state.error = error;
    },
    resetErrors(state) {
      state.errorType = false;
      state.error = null;
    },
  },
});

export const {
  selectById: selectAvailabilityById,
  selectAll: selectAllAvailabilities,
} = availabilityAdapter.getSelectors((state) => state.availability);
export const selectExpertAvailabilities = createSelector(
  selectAllAvailabilities,
  (_, expertId) => expertId,
  (availabilityList, expertId) => availabilityList.filter((availability) => availability.actorId === expertId),
);

export const selectActorAvailabilities = createSelector(
  selectAllAvailabilities,
  (_, actorId) => actorId,
  (availabilityList, actorId) => availabilityList.filter((availability) => Array.isArray(actorId) ? actorId.includes(availability.actorId) : availability.actorId === actorId),
);

export const selectFilteredAvailabilities = createSelector(
  [selectTypeFilterList, selectAllAvailabilities, (state) => state.calendar.selectedEventId],
  (typeFilterList, availabilityList, selectedEventId) => availabilityList.filter((availability) => {
    return availability.availabilityId === DRAFT_AVAILABILITY_ID || availability.availabilityId === selectedEventId || typeFilterList.some((availabilityType) => availability.typeList?.includes(availabilityType.toLowerCase()));
  }),
);
export const selectActorFilteredAvailabilities = createSelector(
  selectFilteredAvailabilities,
  (_, actorId) => actorId,
  (availabilityList, actorId) => {
    return availabilityList.filter((availability) => availability.actorId === actorId);
  },
);
export const {
  listRequest,
  listSuccess,
  listError,
  clear,
  createDraft,
  deleteDraft,
  startEditing,
  edit,
  stopEditing,
  createRequest,
  createSuccess,
  createError,
  updateRequest,
  updateSuccess,
  updateError,
  deleteRequest,
  deleteSuccess,
  deleteError,
  createRecurrenceRequest,
  createRecurrenceSuccess,
  createRecurrenceError,
  resetErrors,
} = availabilitySlice.actions;
export default availabilitySlice.reducer;
